<template>
  <DarwinDialog
    :open="!!currentTab"
    translate="pop-out"
    max-height="90%"
    min-height="90%"
    max-width="80%"
    min-width="80%"
    class="settings-dialog"
    fullscreen
  >
    <div
      v-if="currentTab"
      class="settings"
    >
      <div class="settings__sidebar">
        <div class="settings__sidebar__section-title">Settings</div>
        <SettingsTab
          v-if="membershipId && membershipStore.memberNameById[membershipId]"
          class="settings__sidebar__item"
          :active="currentTab === 'profile'"
          :title="membershipStore.memberNameById[membershipId] ?? ''"
          aria-label="Profile"
          @click="setCurrentTab('profile')"
        >
          <TeamMemberAvatar
            v-if="membershipId"
            :id="membershipId"
            :size="24"
          />
        </SettingsTab>
        <SettingsTab
          class="settings__sidebar__item"
          :active="currentTab === 'team'"
          :title="team ? team.name : ''"
          aria-label="Team Profile"
          @click="setCurrentTab('team')"
        >
          <DarwinAvatar
            v-if="team"
            :id="team.id"
            :name="team.name"
            :url="team.image && (team.image.thumbnail_url || team.image.url)"
            :size="24"
          />
        </SettingsTab>
        <SettingsTab
          v-if="isAuthorized('view_full_datasets')"
          class="settings__sidebar__item"
          title="Members"
          :active="currentTab === 'members'"
          aria-label="Members"
          @click="setCurrentTab('members')"
        >
          <Icon
            name="duotone-persons"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          v-if="isAuthorized('view_team_members')"
          class="settings__sidebar__item"
          title="Security"
          :active="currentTab === 'security'"
          aria-label="Security"
          @click="setCurrentTab('security')"
        >
          <Icon
            name="duotone-lock"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          v-if="isAuthorized('view_customer')"
          class="settings__sidebar__item"
          :active="currentTab === 'plans'"
          title="Plans"
          aria-label="Plans"
          @click="setCurrentTab('plans')"
        >
          <Icon
            name="duotone-credit-card"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          class="settings__sidebar__item"
          title="Storage"
          :active="currentTab === 'storage'"
          aria-label="Storage"
          @click="setCurrentTab('storage')"
        >
          <Icon
            name="duotone-server"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          v-if="isAuthorized('create_api_key')"
          class="settings__sidebar__item"
          title="API Keys"
          :active="currentTab === 'api-keys'"
          aria-label="API Keys"
          @click="setCurrentTab('api-keys')"
        >
          <Icon
            name="duotone-key"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          class="settings__sidebar__item"
          title="Performance"
          :active="currentTab === 'performance'"
          aria-label="Performance"
          @click="setCurrentTab('performance')"
        >
          <Icon
            name="duotone-performance-test"
            class="aside-icon"
          />
        </SettingsTab>
        <SettingsTab
          v-if="isAuthorized('update_dataset')"
          class="settings__sidebar__item"
          title="Labels"
          :active="currentTab === 'labels'"
          @click="setCurrentTab('labels')"
        >
          <Icon
            name="duotone-tag-double"
            class="aside-icon"
          />
        </SettingsTab>
        <div class="settings__sidebar__spacer" />
        <SettingsTab
          class="settings__sidebar__item"
          title="Log Out"
          aria-label="Log Out"
          @click="logout"
        >
          <Icon
            name="duotone-signout"
            class="aside-icon"
          />
        </SettingsTab>
      </div>
      <div class="settings__content">
        <Profile
          v-if="currentTab === 'profile'"
          @close="setCurrentTab(undefined)"
        />
        <Performance
          v-if="currentTab === 'performance'"
          @close="setCurrentTab(undefined)"
        />
        <Labels
          v-if="showLabelsTab"
          @close="setCurrentTab(undefined)"
        />
        <TeamProfile
          v-if="currentTab === 'team'"
          @close="setCurrentTab(undefined)"
        />
        <TeamMembers
          v-if="currentTab === 'members' && isAuthorized('view_full_datasets')"
          @close="setCurrentTab(undefined)"
        />
        <Security
          v-if="currentTab === 'security'"
          @close="setCurrentTab(undefined)"
        />

        <SettingsPaneV2
          v-if="currentTab === 'plans' && billingType === BillingType.CBB"
          header="Plans"
          @close="setCurrentTab(undefined)"
        >
          <template #subheader>View your plan information and usage.</template>
          <template #body>
            <PlansCBB
              :title="'Plans'"
              @close="setCurrentTab(undefined)"
            />
          </template>
        </SettingsPaneV2>
        <SettingsPaneV2
          v-if="currentTab === 'plans' && billingType === BillingType.LBB"
          header="Plans"
          class="settings-pane__plans-self-serve"
          @close="setCurrentTab(undefined)"
        >
          <template #subheader>
            View your plan information and discover add-ons.
            <a
              class="settings-pane__plans-self-serve__link"
              href="https://www.v7labs.com/darwin/pricing"
              target="_blank"
              >Compare plans.</a
            >
          </template>
          <template #body>
            <PlansLBB />
          </template>
        </SettingsPaneV2>

        <Storage
          v-if="currentTab === 'storage'"
          @close="setCurrentTab(undefined)"
        />
        <ApiKeys
          v-if="currentTab === 'api-keys'"
          @close="setCurrentTab(undefined)"
        />
      </div>
    </div>
  </DarwinDialog>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import DarwinAvatar from '@/uiKit/Avatar/DarwinAvatar.vue'
import TeamMemberAvatar from '@/modules/Teams/TeamMemberAvatar.vue'
import SettingsTab from '@/components/Layout/SettingsDialog/SettingsTab.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import { useTeamStore } from '@/pinia/useTeamStore'

import Icon from '@/uiKit/Icon/Icon.vue'
import DarwinDialog from '@/uiKit/Dialog/DarwinDialog.vue'

import PlansCBB from '@/modules/Billing/PlansCBB.vue'
import PlansLBB from '@/modules/Billing/PlansLBB.vue'
import { BillingType, useBillingType } from '@/modules/Billing/useBillingType'
import { usePlanLoader } from '@/modules/Billing/usePlanLoader'

import ApiKeys from './Panes/ApiKeys.vue'
import Performance from './Panes/Performance.vue'
import Labels from './Panes/Labels/Labels.vue'
import Profile from './Panes/Profile.vue'
import Storage from './Panes/Storage.vue'
import TeamMembers from './Panes/TeamMembers/TeamMembers.vue'
import TeamProfile from './Panes/TeamProfile.vue'
import Security from './Panes/Security.vue'
import SettingsPaneV2 from './Panes/SettingsPaneV2.vue'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'
import { ref } from 'vue'

const authStore = useAuthStore()
const userStore = useUserStore()
const teamStore = useTeamStore()
const membershipStore = useMembershipStore()

// used multiple times in the template, so worth shortening
const team = computed(() => teamStore.currentTeam)

const membershipId = computed(() =>
  userStore.currentUser ? membershipStore.memberIdByUserId[userStore.currentUser.id] : undefined,
)

const { isAuthorized } = useAuth()

const currentTab = ref<string>()

const router = useRouter()
const route = useRoute()

const setCurrentTab = (tab: string | undefined): void => {
  router.push({ query: { ...route.query, settings: tab } })
}

const planLoader = usePlanLoader()

const onShow = (): void => {
  planLoader.loadPlan()
}

const showLabelsTab = computed(
  () => currentTab.value === 'labels' && isAuthorized('update_dataset'),
)

watch(
  [
    (): string | undefined => route.query.settings as string | undefined,
    (): boolean => userStore.isAnnotator,
  ],
  ([newTab, isAnnotator], [oldTab, oldIsAnnotator]) => {
    if (newTab === oldTab && isAnnotator === oldIsAnnotator) {
      return
    }

    if (newTab === 'members' && isAnnotator) {
      return router.replace({ query: { ...route.query, settings: 'profile' } })
    }

    currentTab.value = newTab
    if (!oldTab && newTab) {
      onShow()
    }
  },
  { immediate: true },
)

const logout = async (): Promise<void> => {
  await router.push({ query: { ...route.query, settings: undefined } })
  await authStore.logout()
}

const billingType = useBillingType()
</script>

<style lang="scss" scoped>
.settings {
  border-radius: var(--rounded-corner-10);
  overflow: hidden;
  background: var(--color-surface-default);
  display: flex;
  flex-direction: row;
  flex-grow: 1;
}

.settings__sidebar {
  min-width: 240px;
  width: 240px;
  background: var(--color-surface-secondary);
  @include col;
  padding: 8px;
}

.settings__sidebar__section-title {
  @include noSelect;
  margin: 12px 8px;
  color: var(--color-text-default);
  @include typographyBoldBody200;
}

.settings__sidebar__item {
  position: relative;
}

.settings__sidebar__spacer {
  flex: 1 1 auto;
}

.settings__content {
  flex-grow: 1;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
}

.aside-icon {
  color: var(--color-icon-subtle);
  font-size: 18px;
}

// generally, the settings pane components have a center-aligned layout,
// but this one specifically needs to be left aligned, so this css makes the
// override without adding a whole extra property to the pane component
.settings-pane__plans-self-serve {
  :deep(.settings-pane__scroll),
  :deep(.settings-pane__header) {
    align-items: flex-start;
  }
}
.settings-pane__plans-self-serve__link,
.settings-pane__plans-self-serve__link:visited {
  color: var(--color-text-selected);
}
</style>
